import type { User } from "firebase/auth";
import { loadCharacterProfile } from "@/character/storage";
import { equippedPet, loadPetState } from "@/pet/firestore-storage";
import { equippedShadow, loadShadowState } from "@/shadow/firestore-storage";
import { mergeProfileAndShadowGameArt } from "@/shadow/merge-game-art";
import { HOME_ASSETS } from "@/home/home-assets";
import {
  HOME_SCENE_EDITOR_FEATURES,
  mountHomeSceneEditor,
} from "@/home/mount-home-scene-editor";
import {
  HOME_SCENE_ID,
  loadSceneRoom,
  saveSceneRoom,
} from "@/home/scene-room-storage";
import { openDungeonFloorModal } from "@/mount-dungeon-floor-modal";
import { openDungeonModal } from "@/mount-dungeon-modal";
import { parseDungeonFloorFromQuery } from "@/pages/dungeon-routes";
import { openRaidModal } from "@/mount-raid-modal";
import { openRankModal } from "@/mount-rank-modal";
import { openTowerModal } from "@/mount-tower-modal";
import { syncPlayerPublicStats } from "@/cloud/sync-player-stats";

export {
  CREATOR_SCENE_EDITOR_FEATURES,
  HOME_SCENE_EDITOR_FEATURES,
  TOWER_SCENE_EDITOR_FEATURES,
  mountHomeSceneEditor,
  type HomeSceneEditorHandle,
  type MountHomeSceneEditorOptions,
} from "@/home/mount-home-scene-editor";

export async function mountMyHomePage(
  root: HTMLElement,
  user: User,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  const [shadowState, petState] = await Promise.all([
    loadShadowState(user.uid),
    loadPetState(user.uid),
  ]);
  const gameArt = mergeProfileAndShadowGameArt(
    profile,
    equippedShadow(shadowState),
    equippedPet(petState),
  );
  const avatarSrc = gameArt.playerIdleTextureUrl || HOME_ASSETS.avatarDefault;

  void syncPlayerPublicStats(user);

  const handle = await mountHomeSceneEditor(root, {
    user,
    profile,
    profileName: profile.name,
    avatarSrc,
    gameArt,
    sceneId: HOME_SCENE_ID,
    hudSubtitle: "บ้านของฉัน",
    pageRootClass: "game-app page-my-home",
    shell: "home",
    features: HOME_SCENE_EDITOR_FEATURES,
    loadRoom: () => loadSceneRoom(user.uid, HOME_SCENE_ID),
    saveRoom: (data) => saveSceneRoom(user.uid, HOME_SCENE_ID, data),
    skillsQueryStripPath: "/home",
  });

  const params = new URLSearchParams(window.location.search);
  const floorFromQuery = parseDungeonFloorFromQuery(window.location.search);
  const openFromQuery =
    params.get("tower") === "1" ||
    params.get("dungeon") === "1" ||
    params.get("raid") === "1" ||
    params.get("rank") === "1" ||
    floorFromQuery !== null;
  if (params.get("tower") === "1") {
    void openTowerModal(user);
    params.delete("tower");
  }
  if (floorFromQuery !== null) {
    void openDungeonFloorModal(user, floorFromQuery, { returnToDungeonLobby: true });
    params.delete("floor");
    params.delete("dungeon");
  } else if (params.get("dungeon") === "1") {
    void openDungeonModal(user);
    params.delete("dungeon");
  }
  if (params.get("raid") === "1") {
    void openRaidModal(user);
    params.delete("raid");
  }
  if (params.get("rank") === "1") {
    void openRankModal(user);
    params.delete("rank");
  }
  if (openFromQuery) {
    const qs = params.toString();
    const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
    window.history.replaceState({}, "", nextUrl);
  }

  return () => handle.destroy();
}
